import { useCallback } from 'react'

import type { MutationOptions } from '@tanstack/react-query'
import { useMutation as useMutationBase } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'

import { updateMutation } from '@store/mutations/actions'
import type { RootState } from '@store/rootReducer'

const initialState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  error: null,
  data: null,
}
export const useStatefulMutation = <TData, TError, TVariables, TContext>(
  callback: (payload: TVariables) => Promise<TData>,
  {
    mutationKey = [],
    onError,
    onSuccess,
    ...props
  }: MutationOptions<TData, TError, TVariables, TContext>
) => {
  const dispatch = useDispatch()
  const mutationKeyString = mutationKey.join('_')

  const updateState = useCallback(
    (_key: string, payload: Record<string, any>) => {
      if (mutationKeyString) {
        dispatch(updateMutation(mutationKeyString, payload))
      }
    },
    [mutationKeyString, dispatch]
  )

  const mutation = useMutationBase({
    mutationFn: (payload) => {
      updateState('onMutationStart', { ...initialState, isLoading: true })
      return callback(payload)
    },

    ...props,

    onError: (error, ...args) => {
      updateState('onError', { ...initialState, isError: true, error })

      if (onError) {
        return onError(error, ...args)
      }
    },

    onSuccess: async (data, ...args) => {
      if (onSuccess) {
        await onSuccess(data, ...args)
      }

      updateState('onSuccess', { ...initialState, isSuccess: true, data })
    },
  })

  const state: {
    isLoading: boolean
    isSuccess: boolean
    isError: boolean
    error: TError | null
    data: TData | null
    reset: () => void
  } = {
    ...(useSelector(
      ({ mutations }: RootState) => mutations[mutationKeyString]
    ) || initialState),

    reset: useCallback(() => {
      updateState('reset', initialState)
    }, [updateState]),
  }

  return { ...mutation, state }
}
